<script setup>
import Avatar from "primevue/avatar";
import {Head, usePage} from "@inertiajs/vue3";
import {Link} from "@inertiajs/vue3";
import {computed, ref, watch} from "vue";
import _ from "lodash";
import {brightnessByColor, getInitials, getServiceIcon, getServiceName, percentToHex} from "../../../helpers.js";
import ContactDialog from "../components/ui-components/ContactDialog.vue";
import ServiceButton from "../components/ui-components/ServiceButton.vue";
import EmbedMap from "../components/ui-components/EmbedMap.vue";

const props = defineProps({
    forms: Object,
    events: Array,
    location: Object,
    registrationForm: Object,
    locale: String,
})

const previewStyle = computed(() => ({
    backgroundImage: theme.value.background_image_url ? `url(${theme.value.background_image_url})` : '',
    backgroundColor: `#${theme.value.background_color}`,
}));

const contactDialogModel = ref(false);

const darkMode = ref(false);

const theme = computed({
    get() {
        if (darkMode.value) {
            return {
                color: props.forms.theme.color,
                borderless: props.forms.theme.borderless,
                transparent: props.forms.theme.transparent,
                darkMode: true,
                darkModeLock: props.forms.theme.darkModeLock,
                background_color: props.forms.theme.background_color_dark ?? props.forms.theme.background_color,
                background_image_url: props.forms.theme.background_image_url_dark ?? props.forms.theme.background_image_url,
                logo_url: props.forms.theme.logo_url_dark ?? props.forms.theme.logo_url,
                panel_color: props.forms.theme.panel_color_dark ?? props.forms.theme.panel_color,
                panel_transparency: props.forms.theme.panel_transparency_dark ?? props.forms.theme.panel_transparency,
                panel_backdrop_blur: props.forms.theme.panel_backdrop_blur_dark ?? props.forms.theme.panel_backdrop_blur,
                fontFamily: props.forms.theme.fontFamily,
                rows: props.forms.theme.rows,
                columns: props.forms.theme.columns,
                roundness: props.forms.theme.roundness,
            }
        }
        return {
            color: props.forms.theme.color,
            borderless: props.forms.theme.borderless,
            transparent: props.forms.theme.transparent,
            darkMode: false,
            darkModeLock: props.forms.theme.darkModeLock,
            background_color: props.forms.theme.background_color,
            background_image_url: props.forms.theme.background_image_url,
            logo_url: props.forms.theme.logo_url,
            panel_color: props.forms.theme.panel_color,
            panel_transparency: props.forms.theme.panel_transparency,
            panel_backdrop_blur: props.forms.theme.panel_backdrop_blur,
            fontFamily: props.forms.theme.fontFamily,
            rows: props.forms.theme.rows,
            columns: props.forms.theme.columns,
            roundness: props.forms.theme.roundness,
        }
    },
    set(val) {
        Object.assign(props.forms.theme, val);
    }
})
watch(() => theme.value.panel_color, _.debounce((val) => {
    currentBrightness.value = brightnessByColor(val)
}, 150), {immediate: true})


watch(() => props.forms.theme.mode_lock, (val) => {

    if (val === 'auto') {
        if (!window.matchMedia) {
            return;
        }
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        if(query.matches) {
            darkMode.value = true;
            document.querySelector('html')?.classList.add('dark');
        } else {
            darkMode.value = false;
            document.querySelector('html')?.classList.remove('dark');
        }
    } else {
        val === 'light' ? document.querySelector('html')?.classList.remove('dark'): document.querySelector('html')?.classList.add('dark');

        darkMode.value = val === 'dark';
    }
}, {immediate: true})


const currentBrightness = ref(0);

const reservation = usePage()?.props.reservation;

const billItems = computed(() => reservation.bill_items ?? [])
const billTotal = computed(() => billItems.value.reduce((s, i) => s + parseFloat(i.unit_price_with_vat) * parseInt(i.amount), 0))
const billWithoutVat = computed(() => billItems.value.reduce((s, i) => s + parseFloat(i.unit_price_without_vat) * parseInt(i.amount), 0))
const billVat = computed(() => billTotal.value - billWithoutVat.value)

function formatPrice(amount) {
    const loc = props.locale ?? 'cs-CZ'
    return new Intl.NumberFormat(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

function openGoogleCalendar(reservation) {
    const pad = (num) => String(num).padStart(2, '0');

    const formatGoogleDateTime = (date, time) => {
        const [yyyy, mm, dd] = date.split('-');
        const [hh, min] = time.split(':');
        //TODO: Tady je možná problém s timezone
        return `${yyyy}${mm}${dd}T${hh}${min}00`;
    };

    const formName = props.forms.name;
    const serviceName = reservation.services?.name || formName || 'Rezervace';
    const workerName = reservation.workers?.name || '';
    const location = props.location ? getServiceName(props.location) : '';
    const title = `Rezervace – ${serviceName}`;
    const details = `Rezervace ${workerName ? 'u ' + workerName : ''} přes Roori (${formName})`;
    const start = formatGoogleDateTime(reservation.date, reservation.time_start);
    const end = formatGoogleDateTime(reservation.date, reservation.time_end);

    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
    window.open(url, '_blank');
}
</script>

<template>
    <Head :title="forms.name"/>
    <div class="h-full bg-no-repeat bg-cover flex flex-col items-center justify-center p-4 pb-0 min-h-screen" :style="previewStyle">
        <!-- HEADER -->
        <div class="flex items-center justify-center py-9 pb-12">
            <img v-if="theme.logo_url" :src="theme.logo_url" width="200" height="200" />
        </div>
        <!-- BODY -->
        <div class="color-transition transition-all flex items-start justify-center flex-col"
             :class="[{
            'rounded-xl': theme.roundness === 1,
            'rounded-2xl': theme.roundness === 2,
            'rounded-3xl': theme.roundness === 3,
            'backdrop-filter backdrop-blur-lg': theme.panel_backdrop_blur,
            'border': theme.panel_transparency > 50,
            'border-neutral-700': theme.darkMode
        }]"
             :style="{
            backgroundColor: `#${theme.panel_color}${percentToHex(theme.panel_transparency)}`
        }">
            <div class="flex gap-6 p-8 border-b dark:border-neutral-700">
                <i class="pi pi-check-circle text-green-600" style="font-size: 3rem"></i>
                <div class="md:w-[500px]">
                    <!-- Vaše rezervace byla odeslána! -->
                    <p class="text-xl font-semibold">{{ $t('widget.reservation_sent') }}</p>
                    <!-- Na poskytnutý email Vám přijde potvrzení přijetí rezervace systémem. Jak rezervaci schválíme, dostanete další email. -->
                    <p class="text-wrap opacity-70 w-50 overflow-auto">
                      {{ $t('widget.reservation_email_notice') }}
                    </p>
                </div>
            </div>
            <div v-if="location?.type === 'in_person'" class="relative w-full border-b dark:border-neutral-700">
                <EmbedMap :coordinates="{ lat: location?.latitude, lng: location?.longitude }" :color="theme.color" :height="150"/>
            </div>
            <div class="flex w-full flex-col gap-3 p-8">
                <!-- Zákazník -->
                <div class="flex gap-3 items-center">
                    <Avatar :label="getInitials(reservation.name)" size="large" shape="circle"></Avatar>
                    <div>
                        <p class="text-lg font-medium text-gray-800 dark:text-white">{{ reservation.name }}</p>
                        <p class="text-sm opacity-50">{{ reservation.email }}</p>
                    </div>
                </div>

                <!-- Info karta -->
                <div class="p-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm mt-2 flex flex-col gap-3">

                  <!-- Datum / rozsah -->
                  <div class="flex items-center gap-3">
                    <i class="pi pi-calendar text-lg shrink-0" :class="`text-${theme.color}-500`"></i>
                    <div>
                      <p class="font-medium text-gray-800 dark:text-white">
                        <template v-if="reservation.time_start">
                          {{ reservation.formatted_date }}, {{ reservation.time_start.substring(0,5) }} – {{ reservation.time_end.substring(0,5) }}
                        </template>
                        <template v-else-if="reservation.formatted_date_end && reservation.formatted_date_end !== reservation.formatted_date">
                          {{ reservation.formatted_date }} – {{ reservation.formatted_date_end }}
                        </template>
                        <template v-else>
                          {{ reservation.formatted_date }}
                        </template>
                      </p>
                    </div>
                  </div>

                  <!-- Rental item -->
                  <div v-if="reservation.rental_items" class="flex items-center gap-3">
                    <template v-if="reservation.rental_items.gallery?.[0]?.full_url">
                      <img
                        :src="reservation.rental_items.gallery[0].full_url"
                        class="w-10 h-10 rounded-lg object-cover shrink-0"
                        :alt="reservation.rental_items.name"
                      />
                    </template>
                    <template v-else>
                      <div class="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center" :class="`bg-${theme.color}-100 dark:bg-${theme.color}-900/40`">
                        <i class="pi pi-box text-base" :class="`text-${theme.color}-500`"></i>
                      </div>
                    </template>
                    <div class="text-sm min-w-0">
                      <p class="font-medium text-gray-800 dark:text-white truncate">{{ reservation.rental_items.name }}</p>
                      <p v-if="reservation.rental_items.description" class="text-gray-500 dark:text-gray-400 truncate" v-html="reservation.rental_items.description"></p>
                    </div>
                  </div>

                  <!-- Služba + pracovník -->
                  <div v-if="reservation.services" class="flex items-center gap-3">
                    <i class="pi pi-briefcase text-lg shrink-0" :class="`text-${theme.color}-500`"></i>
                    <div class="text-sm text-gray-800 dark:text-gray-300">
                      <p class="font-medium">{{ reservation.services.name }}</p>
                      <p v-if="reservation.workers" class="text-gray-500 dark:text-gray-400">
                        {{ $t('widget.served_by') }} {{ reservation.workers.name }}
                      </p>
                    </div>
                  </div>

                  <!-- Lokace -->
                  <div v-if="reservation.id_reservation_locations" class="flex items-center gap-3">
                    <i :class="`${getServiceIcon(location)} text-${theme.color}-500 text-lg shrink-0`"></i>
                    <div class="text-sm text-gray-800 dark:text-gray-300">
                      <p class="font-medium">{{ location.name }}</p>
                      <p class="text-gray-500 dark:text-gray-400">{{ getServiceName(location) }}</p>
                    </div>
                  </div>

                  <!-- Webcall -->
                  <a v-if="reservation.conference_url" :href="reservation.conference_url" class="flex items-center gap-3">
                    <i :class="`pi pi-video text-${theme.color}-500 text-lg shrink-0`"></i>
                    <div class="text-sm text-gray-800 dark:text-gray-300">
                      <p class="font-medium">Připojit ke schůzce</p>
                      <p class="text-gray-500 dark:text-gray-400 underline truncate">{{ reservation.conference_url }}</p>
                    </div>
                  </a>
                </div>

                <!-- Účtenka (separátní karta) -->
                <div v-if="billItems.length" class="p-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm">
                  <p class="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">{{ $t('widget.bill') }}</p>
                  <table class="w-full text-sm">
                    <tbody>
                      <tr v-for="item in billItems" :key="item.id" class="border-b border-gray-100 dark:border-neutral-800 last:border-0">
                        <td class="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{{ item.description }}</td>
                        <td class="text-right py-1.5 px-2 text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">{{ item.amount }}×</td>
                        <td class="text-right py-1.5 pl-2 text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">{{ formatPrice(parseFloat(item.unit_price_with_vat) * parseInt(item.amount)) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="mt-3 pt-3 border-t dark:border-neutral-700 text-sm space-y-1">
                    <div v-if="billVat > 0.01" class="flex justify-between text-gray-500 dark:text-gray-400">
                      <span>{{ $t('widget.bill_without_vat') }}</span>
                      <span class="tabular-nums">{{ formatPrice(billWithoutVat) }}</span>
                    </div>
                    <div v-if="billVat > 0.01" class="flex justify-between text-gray-500 dark:text-gray-400">
                      <span>{{ $t('widget.bill_vat') }}</span>
                      <span class="tabular-nums">{{ formatPrice(billVat) }}</span>
                    </div>
                    <div class="flex justify-between font-semibold text-gray-800 dark:text-white text-base">
                      <span>{{ $t('widget.bill_total') }}</span>
                      <span class="tabular-nums">{{ formatPrice(billTotal) }}</span>
                    </div>
                  </div>
                </div>
            </div>

            <div class="flex flex-wrap gap-3 p-8 pt-0">
                <service-button horizontal @click="openGoogleCalendar(reservation)" icon="pi pi-calendar-plus" :title="$t('widget.add_to_calendar')" :color="theme.color"></service-button>
                <Link :href="$page.props.editUrl" class="max-sm:w-full"><service-button horizontal icon="pi pi-pencil" :title="$t('widget.edit_reservation')" :color="theme.color"></service-button></Link>
                <service-button horizontal icon="pi pi-send" @click="contactDialogModel = true" :title="$t('widget.send_message')" :color="theme.color"></service-button>
            </div>
            <contact-dialog v-model:visible="contactDialogModel" :url="`/send-contact-form/${reservation.id}`"></contact-dialog>
        </div>
        <div class="flex items-center justify-center py-9 pb-12 opacity-0">
            <img v-if="theme.logo_url" :src="theme.logo_url" width="200" height="100" />
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
